// Desktop Properties View - matching admin-web layout with data table
import React, { useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { PropertiesDataTable } from '../PropertiesDataTable';
import { IPropertyRowData, IProperty } from '@/types/admin/properties';
import { downloadCsvFrom2DArray } from '@/utils/admin/csvExport';
import { DesktopPropertyDetailsPanel } from './DesktopPropertyDetailsPanel';
import { DesktopCreatePropertyPanel } from './DesktopCreatePropertyPanel';

interface DesktopPropertiesViewProps {
  data: {
    rows: IPropertyRowData[];
    count: number;
  };
  isLoading: boolean;
  csvData: (string | number)[][];
  selectedProperty: IProperty | undefined;
  showCreatePanel: boolean;
  onPropertySelect: (property: IPropertyRowData) => void;
  onCreateProperty: () => void;
  onUpdateCsvData?: (data: IPropertyRowData[]) => void;
  onClosePanel: () => void;
  onCloseCreatePanel: () => void;
  onCreateSuccess?: () => void;
}

export const DesktopPropertiesView: React.FC<DesktopPropertiesViewProps> = ({
  data,
  isLoading,
  csvData,
  selectedProperty,
  showCreatePanel,
  onPropertySelect,
  onCreateProperty,
  onUpdateCsvData,
  onClosePanel,
  onCloseCreatePanel,
  onCreateSuccess,
}) => {
  const hasData = data.count > 0;

  // Handle CSV export
  const handleExportTable = () => {
    if (Platform.OS === 'web') {
      const filename = `resio-properties-${new Date().toJSON().slice(0, 10)}`;
      try {
        downloadCsvFrom2DArray(csvData, filename);
      } catch (e) {
        console.error('Failed to download CSV', e);
      }
    } else {
      console.log('CSV export is available on web only.');
    }
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Properties"
        subtitle=""
        showAddButton
        onAddPress={onCreateProperty}
        addButtonText="Create Property"
        showExportButton={hasData}
        onExportPress={handleExportTable}
        exportButtonText="Export Table"
      />

      <View style={styles.contentRow}>
        <View style={styles.tableContainer}>
          <PropertiesDataTable
            data={data}
            isLoading={isLoading}
            onPropertySelect={onPropertySelect}
            updateCsvData={onUpdateCsvData}
          />
        </View>

        {selectedProperty && (
          <DesktopPropertyDetailsPanel
            visible={true}
            property={selectedProperty}
            onClose={onClosePanel}
          />
        )}
      </View>

      {/* Create Property Panel */}
      <DesktopCreatePropertyPanel
        visible={showCreatePanel}
        onClose={onCloseCreatePanel}
        onSuccess={onCreateSuccess}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  contentRow: {
    flex: 1,
    minHeight: 0,
  },
  tableContainer: {
    flex: 1,
    minHeight: 0,
  },
});

